/**
 * @file The Notifications page: a header with bulk actions (mark all read or
 * unread, delete all) and the All/Unread view switch above the notification
 * list. Consumer: the `/notifications` route. The header row and its action
 * group both wrap, so at phone widths the actions drop below the title and
 * then onto further lines instead of widening the page.
 */
import { FC, useState, useContext } from 'react';
import { Typography, Segmented, Button, Popconfirm } from 'antd';

import Navb from '../components/Navb';
import FlexPage from '../components/atoms/FlexPage';
import FlexContent from '../components/atoms/FlexContent';
import NotificationsTable from '../components/notifications/NotificationsTable';
import { MAX_LIMITED_WIDTH_VIEWPORT_NARROW } from '#types/System.Parameters.types';
import FlexBox from '../components/atoms/FlexBox';
import { UserContext } from '../providers/UserContext';
import Icon from '../components/atoms/Icon';

const { Title, Text } = Typography;

type ViewType = 'Unread' | 'All';

const NotificationsPage: FC = () => {
    const [view, setView] = useState<ViewType>('All');

    const {
        notificationBundles,
        markAllNotificationsRead,
        markAllNotificationsUnread,
        deleteAllNotifications,
    } = useContext(UserContext);

    const hasUnread = (notificationBundles?.unreadNotifications?.length ?? 0) > 0;
    const hasRead = (notificationBundles?.readNotifications?.length ?? 0) > 0;
    const hasAny = hasUnread || hasRead;

    return (
        <FlexPage>
            <Navb />
            <FlexContent style={{ maxWidth: MAX_LIMITED_WIDTH_VIEWPORT_NARROW }}>
                <FlexBox
                    noGrow
                    stretch
                    wrap
                    gap={20}
                >
                    <Title
                        level={4}
                        style={{ margin: 0 }}
                    >
                        Notifications
                    </Title>
                    <FlexBox noGrow wrap gap={5}>
                        <Button
                            type="text"
                            icon={<Icon.CheckCircle />}
                            disabled={!hasUnread}
                            aria-label="Mark all notifications as read"
                            onClick={markAllNotificationsRead}
                        >
                            Mark all read
                        </Button>
                        <Button
                            type="text"
                            icon={<Icon.Circle />}
                            disabled={!hasRead}
                            aria-label="Mark all notifications as unread"
                            onClick={markAllNotificationsUnread}
                        >
                            Mark all unread
                        </Button>
                        <Popconfirm
                            title="Delete all notifications?"
                            description="This action cannot be undone."
                            onConfirm={deleteAllNotifications}
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            cancelText="Cancel"
                            disabled={!hasAny}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<Icon.Trash2 />}
                                disabled={!hasAny}
                                aria-label="Delete all notifications"
                            >
                                Delete all
                            </Button>
                        </Popconfirm>
                        <Segmented
                            options={['All', 'Unread']}
                            value={view}
                            onChange={(value) => setView(value as ViewType)}
                        />
                    </FlexBox>
                </FlexBox>
                <NotificationsTable view={view} />
                {view === 'Unread' && notificationBundles?.readNotifications && notificationBundles.readNotifications.length > 0 && (
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setView('All')}
                    >
                        <Text type="secondary">
                            {notificationBundles.readNotifications.length} read notification{notificationBundles.readNotifications.length !== 1 ? 's' : ''}
                        </Text>
                    </Button>
                )}
            </FlexContent>
        </FlexPage>
    );
};

export default NotificationsPage; 